import React, { useEffect, useState } from 'react';
import * as Routes from '@/modules/routes';
import axios from 'axios';
import Spinner from '@/components/common/Spinner';
import I18n from 'i18n-js';
import moment from 'moment';
import { clsx } from 'clsx';
import AssignmentCard from './AssignmentCard';
import { UserType } from '../../types';
import styles from './AssignmentCards.module.scss';

interface AssignmentCardsProps {
  classroomId: number;
  subsection: 'upcoming' | 'overdue';
  programId: number;
  user: UserType;
  onAssignmentsLoad?: (subsection: string, count: number) => void;
}

const AssignmentCards = ({ classroomId, subsection, programId, user, onAssignmentsLoad }: AssignmentCardsProps) => {
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [useSubsections, setUseSubsections] = useState(false);

  const isStudent = user.type === 'Student';
  const isReadingOnly = assignment => Object.keys(assignment.assignment_models_hash).every(key => key === 'Section');
  const toDo = assignment => assignment.status === 'to_do';
  const pastDue = assignment => (assignment.status === 'past_due' && !isReadingOnly(assignment));

  const studentFilters = (assignment: any) => {
    if (subsection === 'upcoming') return toDo(assignment);
    if (subsection === 'overdue') return pastDue(assignment);
  };

  // If the user is a student, show assignments that have the to do status and have not passed their due date
  const getFilterOptions = assignment => (
    isStudent ? studentFilters(assignment) : (new Date(assignment.due_datetime) >= new Date())
  );

  useEffect(() => {
    const route = isStudent ? Routes.plato_api_student_index_student_assignments_path(
      user.id,
      {
        for_classroom: classroomId,
        in_program: programId,
      },
    ) : Routes.plato_api_staffer_assignments_path(
      user.id,
      {
        in_program: programId,
        local_time_zone: moment.tz.guess(),
      },
    );

    axios.get(route)
      .then((response) => {
        setLoading(false);
        const filteredAssignments = response.data.data.filter(assignment => getFilterOptions(assignment));
        setAssignments(filteredAssignments);
        setUseSubsections(isStudent && response.data.data.some(a => pastDue(a)));
        onAssignmentsLoad?.(subsection, filteredAssignments.length);
      });
  }, []);

  const renderNoAssignmentsMessage = () => (
    <div className="tw-w-full tw-flex tw-flex-col tw-justify-center tw-items-center">
      {isStudent && subsection === 'upcoming' && (
        <i className="fa fa-icon fa-check-circle tw-text-green tw-text-4xl tw-mb-1" />
      )}
      <div className="tw-text-[18px]">
        {!isStudent ? (
          I18n.t('you_have_no_current_assignments')
        ) : (
          subsection === 'upcoming' && I18n.t('you_have_no_upcoming_assignments')
        )}
      </div>
    </div>
  );

  const getAssignmentCards = () => (
    assignments
      .sort((a, b) => {
        const dueDateA = new Date(a.due_datetime);
        const dueDateB = new Date(b.due_datetime);
        return dueDateA.getTime() - dueDateB.getTime();
      })
      .map(assignment => (
        <AssignmentCard
          key={`assignment-card-${assignment.id}`}
          assignment={assignment}
          assignmentModelsHash={assignment.assignment_models_hash}
          isOverdue={subsection === 'overdue'}
          user={user}
        />
      ))
  );

  const getSubsection = () => {
    if (subsection === 'overdue' && assignments.length === 0) return <></>;

    const label = (subsection === 'overdue') ? I18n.t('overdue') : I18n.t('upcoming');
    const subsectionText = `${label} (${assignments.length})`;

    return (
      <>
        {useSubsections && (
          <div className={styles.cardHeader}>
            <i className={clsx('tw-text-gray-500', {
              'fa fa-clock-o': subsection === 'upcoming',
              'fa fa-times-circle': subsection === 'overdue',
            })}
            />
            <span className="tw-pl-2 tw-text-gray-600">{subsectionText}</span>
          </div>
        )}
        {assignments.length > 0 ? getAssignmentCards() : renderNoAssignmentsMessage()}
      </>
    );
  };

  if (loading) return <div className="tw-flex tw-justify-center tw-items-center"><Spinner /></div>;

  return (
    <>
      <div className={styles.subsection}>
        {getSubsection()}
      </div>
    </>
  );
};

export default AssignmentCards;
